import { z } from '@customerio/hydra-validation';
import { isEmptyString } from '@/utils/string';

/**
 * The attribute editor's form state: one row per attribute, plus the names
 * whose removal is pending. The rules that validate a row depend on the
 * workspace, so the schema is built per workspace with
 * {@link buildAttributesFormSchema}.
 */

const AttributeRowSchema = z.object({
  /** The attribute name as it currently reads. */
  key: z.string(),
  value: z.string(),
  /** The name the row loaded with; `''` for a row created in this session. */
  originalKey: z.string(),
});

export type AttributeRow = z.infer<typeof AttributeRowSchema>;

const AttributesFormSchema = z.object({
  attributes: z.array(AttributeRowSchema),
  /**
   * Names sent as empty strings, which the API treats as removing the
   * attribute. A removed row's name lands here; renaming a row hides the name
   * it loaded with.
   */
  hiddenAttrs: z.array(z.string()),
});

export type AttributesFormInput = z.input<typeof AttributesFormSchema>;
export type AttributesFormOutput = z.output<typeof AttributesFormSchema>;

export interface AttributeValidationRules {
  /** The workspace's identifiers. When there is exactly one, its value cannot be blank. */
  requiredAttributes: readonly string[];
  /** Names already on the profile, which a renamed or new row cannot take. */
  permanentAttributes: readonly string[];
  /** Names the backend assigns, which a renamed or new row cannot take. */
  autoGeneratedAttributes: readonly string[];
}

export interface AttributeRowErrors {
  key?: string;
  value?: string;
}

export function isAttributeKeyChanged(row: Pick<AttributeRow, 'key' | 'originalKey'>): boolean {
  return row.key !== row.originalKey;
}

/** A row added in this session, as opposed to one the profile loaded with. */
export function isCreatedAttributeRow(row: Pick<AttributeRow, 'originalKey'>): boolean {
  return row.originalKey === '';
}

/** Matches the server's own check: the Unicode replacement character is the only invalid one. */
export function hasInvalidCharacters(value: string): boolean {
  return value.includes('�');
}

/** The validation errors on one row; later checks override earlier ones for the same field. */
export function attributeRowErrors(row: AttributeRow, rules: AttributeValidationRules): AttributeRowErrors {
  const errors: AttributeRowErrors = {};
  const { key, value } = row;
  const trimmedKey = key.trim();

  // With several identifiers a person only needs one of them, so only a lone identifier is required.
  if (rules.requiredAttributes.length === 1 && rules.requiredAttributes.includes(key) && isEmptyString(value)) {
    errors.value = "Attribute value can't be blank";
  }

  if (isEmptyString(key) && !isEmptyString(value)) {
    errors.key = "Attribute name can't be blank";
  }

  if (hasInvalidCharacters(key)) {
    errors.key = 'Attribute name contains invalid characters';
  }

  if (isAttributeKeyChanged(row)) {
    if (rules.autoGeneratedAttributes.includes(trimmedKey)) {
      errors.key = `${trimmedKey} is auto-generated in this workspace`;
    } else if (rules.permanentAttributes.includes(trimmedKey) || rules.requiredAttributes.includes(trimmedKey)) {
      errors.key = 'Attribute name is already used';
    }
  }

  return errors;
}

/** The form schema for one workspace: the row shape plus that workspace's naming rules. */
export function buildAttributesFormSchema(rules: AttributeValidationRules) {
  return AttributesFormSchema.superRefine((form, ctx) => {
    form.attributes.forEach((row, index) => {
      const errors = attributeRowErrors(row, rules);
      if (errors.key != null) {
        ctx.addIssue({ code: 'custom', path: ['attributes', index, 'key'], message: errors.key });
      }
      if (errors.value != null) {
        ctx.addIssue({ code: 'custom', path: ['attributes', index, 'value'], message: errors.value });
      }
    });
  });
}

export type AttributesFormSchema = ReturnType<typeof buildAttributesFormSchema>;

/**
 * The attributes to send: every named row, then the hidden names emptied so
 * the API removes them. A hidden name wins over a row with the same name.
 */
export function toCustomerAttributes({ attributes, hiddenAttrs }: AttributesFormOutput) {
  const named = attributes.filter((row) => row.key !== '').map((row): [string, string] => [row.key, row.value]);
  const hidden = hiddenAttrs.map((key): [string, string] => [key, '']);
  return Object.fromEntries([...named, ...hidden]);
}

export type CustomerAttributes = ReturnType<typeof toCustomerAttributes>;

/** Adding a catalog attribute back cancels any pending removal of that name. */
export function hiddenAttrsAfterAdd(hiddenAttrs: readonly string[], key: string): string[] {
  return hiddenAttrs.filter((hidden) => hidden !== key);
}

/**
 * Removing a row queues its current name for removal on save. A row created in
 * this session has no name on the profile to remove, so nothing is queued.
 */
export function hiddenAttrsAfterRemove(
  hiddenAttrs: readonly string[],
  row: Pick<AttributeRow, 'key' | 'originalKey'>,
): string[] {
  if (isCreatedAttributeRow(row)) {
    return [...hiddenAttrs];
  }
  return [...hiddenAttrs, row.key];
}

/**
 * The hidden names after the row at `index` was renamed: its new name is no
 * longer hidden, and the name it loaded with is hidden unless the row still
 * carries that name or another row was itself renamed to it.
 */
export function hiddenAttrsAfterRename(form: AttributesFormInput, index: number): string[] {
  const row = form.attributes[index];
  if (row == null) {
    return form.hiddenAttrs;
  }

  const hidden = form.hiddenAttrs.filter((key) => key !== row.key);
  // A row typed back to the name it loaded with is not renamed, so its own name
  // must not stay queued for removal.
  if (isCreatedAttributeRow(row) || !isAttributeKeyChanged(row)) {
    return hidden;
  }

  const rowWithOriginalKey = form.attributes.find((candidate) => candidate.key === row.originalKey);
  if (rowWithOriginalKey != null && isAttributeKeyChanged(rowWithOriginalKey)) {
    return hidden;
  }

  // Each keystroke re-queues the name, so a name is only ever listed once.
  if (hidden.includes(row.originalKey)) {
    return hidden;
  }

  return [...hidden, row.originalKey];
}
